import React, { useEffect, useState } from 'react';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { PhoneNumberField } from '@/components/common';
import { getAccountsGroups } from '../../services/testService';
import type {
  NotificationChannel,
  NotificationTestFormValues,
} from '../../types/notificationTest';
import { RECIPIENT_TYPE_CHOICES } from '../../constants';

const boxedSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '12px',
  p: 2,
  mb: 2,
};

interface RecipientsSectionProps {
  channel: NotificationChannel;
}

export const RecipientsSection: React.FC<RecipientsSectionProps> = ({
  channel,
}) => {
  const translate = useTranslate();
  const { control } = useFormContext<NotificationTestFormValues>();
  const labels = (() => {
    switch (channel) {
      case 'telegram':
        return {
          itemKey: 'resources.test.fields.chatId',
          addKey: 'resources.test.actions.addChatIds',
          requiredKey: 'resources.test.validation.chatIdRequired',
        };
      case 'sms':
      case 'whatsapp':
        return {
          itemKey: 'resources.test.fields.number',
          addKey: 'resources.test.actions.addNumbers',
          requiredKey: 'resources.test.validation.numberRequired',
        };
      default:
        return {
          itemKey: 'resources.test.fields.address',
          addKey: 'resources.test.actions.addAddresses',
          requiredKey: 'resources.test.validation.addressRequired',
        };
    }
  })();
  const itemFieldKey = labels.itemKey;
  const addActionKey = labels.addKey;
  const requiredMessageKey = labels.requiredKey;

  const recipientType = useWatch({ control, name: 'recipients.type' });
  const accountId = useWatch({ control, name: 'accountId' });
  const appId = useWatch({ control, name: 'appId' });

  const {
    fields: addressFields,
    append: appendAddress,
    remove: removeAddress,
  } = useFieldArray({
    control,
    name: 'recipients.addresses',
  });

  const [groups, setGroups] = useState<string[]>([]);

  useEffect(() => {
    if (!accountId || !appId) {
      setGroups([]);
      return;
    }
    let cancelled = false;
    getAccountsGroups({ accountId, application: appId })
      .then((res) => {
        if (!cancelled) setGroups(res);
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, appId]);

  return (
    <>
      <Controller
        name="recipients.type"
        control={control}
        rules={{
          required: translate(
            'resources.test.validation.recipientTypeRequired'
          ),
        }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            select
            fullWidth
            required
            sx={{ mb: 2 }}
            label={translate('resources.test.fields.recipientType')}
            InputLabelProps={{ shrink: true }}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message}
          >
            {RECIPIENT_TYPE_CHOICES.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {translate(c.labelKey)}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      {/* GROUP targets a group — individual addresses are hidden then. */}
      {recipientType !== 'GROUP' && (
      <Box sx={boxedSx}>
        <Button
          variant="contained"
          size="small"
          onClick={() => appendAddress({ value: '' })}
          sx={{
            mb: 2,
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {translate(addActionKey)}
        </Button>
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          {addressFields.map((f, i) => (
            <Grid item xs={12} sm={6} md={4} key={f.id}>
              <Box
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
              >
                <Controller
                  name={`recipients.addresses.${i}.value` as const}
                  control={control}
                  rules={{
                    validate: (value) =>
                      (value ?? '').trim() !== '' ||
                      translate(requiredMessageKey),
                  }}
                  render={({ field, fieldState }) =>
                    channel === 'sms' || channel === 'whatsapp' ? (
                      <PhoneNumberField
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        required
                        label={`${translate(itemFieldKey)} ${i + 1}`}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                      />
                    ) : (
                      <TextField
                        {...field}
                        fullWidth
                        required
                        placeholder={`${translate(itemFieldKey)} ${i + 1}`}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                      />
                    )
                  }
                />
                <IconButton
                  onClick={() => removeAddress(i)}
                  disabled={addressFields.length <= 1}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    flexShrink: 0,
                    mt: 1,
                  }}
                  aria-label="remove address"
                >
                  <CloseIcon
                    color={
                      addressFields.length <= 1 ? 'disabled' : 'error'
                    }
                  />
                </IconButton>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
      )}

      {recipientType === 'GROUP' && (
        <Controller
          name="recipients.groupId"
          control={control}
          rules={{
            required: translate('resources.test.validation.groupRequired'),
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              select
              fullWidth
              required
              sx={{ mb: 2 }}
              label={translate('resources.test.fields.groupId')}
              InputLabelProps={{ shrink: true }}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            >
              {groups.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      )}
    </>
  );
};

export default RecipientsSection;
